import React from 'react';
import { Link } from 'react-router-dom';
import { Tag, Heart, AlertCircle } from 'lucide-react';
import { useWishlist } from '../context/WishlistContext';

function stockPill(product) {
  const s = product.stock_status || (product.stock <= 0 ? 'out_of_stock' : product.stock <= 3 ? 'low_stock' : 'in_stock');
  if (s === 'out_of_stock') {
    return { label: 'Out of stock',                       cls: 'bg-red-600 text-white' };
  }
  if (s === 'low_stock') {
    const n = typeof product.stock === 'number' ? product.stock : null;
    return { label: n != null ? `Only ${n} left` : 'Low stock', cls: 'bg-amber-500 text-white' };
  }
  return null;
}

export default function ProductCard({ product, onOpen }) {
  const { has, toggle } = useWishlist();
  const liked   = has(product.id);
  const badge   = stockPill(product);
  const soldOut = (product.stock_status || (product.stock <= 0 ? 'out_of_stock' : '')) === 'out_of_stock';

  return (
    <div className="group relative">
      <button
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggle(product); }}
        data-testid={`wishlist-toggle-${product.id}`}
        className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-white/95 grid place-items-center shadow-md hover:scale-110 transition-transform"
        aria-label={liked ? 'Remove from wishlist' : 'Add to wishlist'}
      >
        <Heart className={`w-4 h-4 transition-colors ${liked ? 'fill-red-500 text-red-500' : 'text-neutral-600'}`} />
      </button>

      <Link to={`/product/${product.slug}`} className="block cursor-pointer">
        <div className="relative overflow-hidden rounded-md bg-neutral-100 aspect-[4/5]">
          <img
            src={product.image}
            alt={product.title}
            className={`w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 ${soldOut ? 'grayscale opacity-70' : ''}`}
            loading="lazy"
          />
          {/* Save pill */}
          {product.save > 0 && !soldOut && (
            <span className="absolute bottom-3 left-3 bg-[#b8935a] text-white text-xs font-semibold px-2.5 py-1 rounded flex items-center gap-1">
              <Tag className="w-3 h-3" /> SAVE Rs. {product.save.toLocaleString('en-IN')}
            </span>
          )}
          {/* Stock badge (top-left) */}
          {badge && (
            <span
              data-testid={`stock-badge-${product.id}`}
              className={`absolute top-3 left-3 text-[11px] font-semibold px-2.5 py-1 rounded-full flex items-center gap-1 shadow-sm ${badge.cls}`}
            >
              <AlertCircle className="w-3 h-3" /> {badge.label}
            </span>
          )}
          {soldOut && (
            <div className="absolute inset-0 grid place-items-center">
              <span className="bg-white/95 text-red-700 font-bold tracking-widest text-xs px-4 py-1.5 rounded-md border border-red-200 shadow">
                SOLD OUT
              </span>
            </div>
          )}
        </div>
        <h3 className="mt-3 font-serif text-lg text-neutral-900 line-clamp-1 group-hover:text-[#b8935a] transition-colors">{product.title.split('|')[0]}</h3>
        <div className="mt-1 flex items-baseline gap-2 flex-wrap">
          <span className="font-bold text-neutral-900">From Rs. {product.price.toLocaleString('en-IN')}.00</span>
          <span className="text-neutral-500 line-through text-sm">Rs. {product.originalPrice.toLocaleString('en-IN')}.00</span>
        </div>
      </Link>
      <button
        onClick={() => !soldOut && onOpen(product)}
        disabled={soldOut}
        data-testid={`choose-options-${product.id}`}
        className={`mt-3 w-full font-semibold py-2.5 rounded-md text-sm tracking-wide transition-colors ${
          soldOut
            ? 'bg-neutral-200 text-neutral-500 cursor-not-allowed'
            : 'bg-[#1e3a2b] hover:bg-[#2a5040] text-white'
        }`}
      >
        {soldOut ? 'Notify me' : 'Choose options'}
      </button>
    </div>
  );
}
